import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { batch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  reloadDashboardTemplates,
  dashboardInstanceToggleFavorite,
} from '../../../../../../redux/slices/dashboards/dashboardInstance';
import { FAVORITE_VERSION_ENTITY } from '../../../../../../utils/apollo/favorite/mutations';
import {
  FavoriteEntityType,
  IFavoriteVersionEntityMutation,
  IFavoriteVersionEntityMutationVariables,
} from '../../../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../../../utils/apollo/printGraphqlErrors';
import { useAppDispatch } from '../../../../../../redux/hooks';
import { assertNotNil } from '../../../../../../utils/types';
import toastMessage from '../../../../../../utils/toastMessage';
import { appRoutes } from '../../../../../../path';

interface IUseFavoriteDashboardArgs {
  baseId: string;
  isFavorite: boolean;
}

export const useFavoriteDashboardTemplate = ({
  baseId,
  isFavorite,
}: IUseFavoriteDashboardArgs) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isCurrent = location.pathname.startsWith(
    appRoutes.dashboard.generateLink(baseId),
  );

  const handleMutationComplete = ({
    favoriteVersionEntity,
  }: IFavoriteVersionEntityMutation) => {
    const { errors, message } = favoriteVersionEntity;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(message);
    toastMessage({ message, type: 'info' });

    batch(() => {
      if (isCurrent) {
        dispatch(dashboardInstanceToggleFavorite());
      }

      dispatch(reloadDashboardTemplates());
    });
  };

  const [setFavorite] = useMutation<
    IFavoriteVersionEntityMutation,
    IFavoriteVersionEntityMutationVariables
  >(FAVORITE_VERSION_ENTITY, {
    onCompleted: handleMutationComplete,
  });

  return useCallback(() => {
    return setFavorite({
      variables: {
        input: {
          entityId: baseId,
          entityType: FavoriteEntityType.Dashboard,
          isFavorite: !isFavorite,
        },
      },
    });
  }, [baseId, isFavorite, setFavorite]);
};
